import { ArrowUpRight } from "lucide-react";

import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { useTheme } from "@mui/material";
import ButtonBase from "@mui/material/ButtonBase";
import Stack from "@mui/material/Stack";

import { useDispatch } from "@src/store";
import { VivIdeasAction } from "@src/store/slices/experienceBuilder/ideas";
import type { Idea } from "@src/store/slices/experienceBuilder/ideas";

import { CATEGORY_LABELS } from "./categories";

/** One idea row; clicking it sends the idea and dismisses the library. */
export const IdeaLibraryItem = ({
  idea,
  disabled,
  showCategory,
}: {
  idea: Idea;
  /** Mid-turn, when sending would be a no-op. */
  disabled: boolean;
  /** Search results span categories, so the row names its own. */
  showCategory: boolean;
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const textPrimary = theme.palette.primary.main;
  const textSecondary = theme.palette.secondary.main;

  return (
    <ButtonBase
      disabled={disabled}
      onClick={() =>
        void dispatch(VivIdeasAction.sendIdea({ idea, source: "library" }))
      }
      sx={{
        justifyContent: "flex-start",
        alignItems: "flex-start",
        textAlign: "left",
        gap: 2,
        px: 4,
        py: 3,
        color: textSecondary,
        // Arrow default lives here (a class rule), not inline on the icon — an
        // inline `opacity: 0` would beat the `:hover` rule.
        "& .idea-arrow": { opacity: 0, color: "inherit" },
        "&:hover": {
          backgroundColor: theme.igBackgroundColors.interactive.neutral.hover,
          color: textPrimary,
        },
        "&:hover .idea-arrow": { opacity: 1 },
      }}
    >
      <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
        <IgTypography variant="body2" color="inherit">
          {idea.title}
        </IgTypography>
        {showCategory && (
          <IgTypography variant="caption" color={theme.palette.text.tertiary}>
            {CATEGORY_LABELS[idea.category]}
          </IgTypography>
        )}
      </Stack>
      <ArrowUpRight
        size={16}
        className="idea-arrow"
        style={{ flexShrink: 0 }}
      />
    </ButtonBase>
  );
};
